import { useRef } from "react";
import { COURT_DIMENSIONS } from "./courtConstants";
import { courtToWorld } from "./courtUtils";

// Professional Basketball Court Component
export const BasketballCourt = () => {
	const courtGroup = useRef();

	// Court surface
	const CourtSurface = () => (
		<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
			<planeGeometry args={[COURT_DIMENSIONS.WIDTH, COURT_DIMENSIONS.HEIGHT]} />
			<meshStandardMaterial color="#233442" />
		</mesh>
	);

	// Court lines with proper thickness
	const CourtLines = () => {
		const lineMaterial = (
			<meshStandardMaterial
				color="#ffffff"
				polygonOffset
				polygonOffsetFactor={-1}
				polygonOffsetUnits={-1}
			/>
		);

		return (
			<group>
				{/* Boundary lines - Top */}
				<mesh
					position={[0, 0.01, COURT_DIMENSIONS.HEIGHT / 2]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[COURT_DIMENSIONS.WIDTH, COURT_DIMENSIONS.LINE_WIDTH, 0.1]}
					/>
					{lineMaterial}
				</mesh>

				{/* Boundary lines - Bottom */}
				<mesh
					position={[0, 0.01, -COURT_DIMENSIONS.HEIGHT / 2]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[COURT_DIMENSIONS.WIDTH, COURT_DIMENSIONS.LINE_WIDTH, 0.1]}
					/>
					{lineMaterial}
				</mesh>

				{/* Boundary lines - Left */}
				<mesh
					position={[-COURT_DIMENSIONS.WIDTH / 2, 0.01, 0]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[COURT_DIMENSIONS.LINE_WIDTH, COURT_DIMENSIONS.HEIGHT, 0.1]}
					/>
					{lineMaterial}
				</mesh>

				{/* Boundary lines - Right */}
				<mesh
					position={[COURT_DIMENSIONS.WIDTH / 2, 0.01, 0]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[COURT_DIMENSIONS.LINE_WIDTH, COURT_DIMENSIONS.HEIGHT, 0.1]}
					/>
					{lineMaterial}
				</mesh>

				{/* Key (paint) area - Top */}
				<mesh
					position={[
						0,
						0.01,
						-COURT_DIMENSIONS.HEIGHT / 2 + COURT_DIMENSIONS.KEY_HEIGHT,
					]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[
							COURT_DIMENSIONS.KEY_WIDTH,
							COURT_DIMENSIONS.LINE_WIDTH,
							0.1,
						]}
					/>
					{lineMaterial}
				</mesh>

				{/* Key (paint) area - Left */}
				<mesh
					position={[
						-COURT_DIMENSIONS.KEY_WIDTH / 2,
						0.01,
						-COURT_DIMENSIONS.HEIGHT / 2 + COURT_DIMENSIONS.KEY_HEIGHT / 2,
					]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[
							COURT_DIMENSIONS.LINE_WIDTH,
							COURT_DIMENSIONS.KEY_HEIGHT,
							0.1,
						]}
					/>
					{lineMaterial}
				</mesh>

				{/* Key (paint) area - Right */}
				<mesh
					position={[
						COURT_DIMENSIONS.KEY_WIDTH / 2,
						0.01,
						-COURT_DIMENSIONS.HEIGHT / 2 + COURT_DIMENSIONS.KEY_HEIGHT / 2,
					]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[
							COURT_DIMENSIONS.LINE_WIDTH,
							COURT_DIMENSIONS.KEY_HEIGHT,
							0.1,
						]}
					/>
					{lineMaterial}
				</mesh>

				{/* Free throw line */}
				<mesh
					position={[
						0,
						0.01,
						-COURT_DIMENSIONS.HEIGHT / 2 +
							COURT_DIMENSIONS.FREE_THROW_LINE_DISTANCE,
					]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<boxGeometry
						args={[
							COURT_DIMENSIONS.KEY_WIDTH,
							0.1,
							COURT_DIMENSIONS.LINE_WIDTH,
						]}
					/>
					{lineMaterial}
				</mesh>

				{/* Free throw circle */}
				<mesh
					position={[
						0,
						0.01,
						-COURT_DIMENSIONS.HEIGHT / 2 +
							COURT_DIMENSIONS.FREE_THROW_LINE_DISTANCE,
					]}
					rotation={[-Math.PI / 2, 0, 0]}
				>
					<ringGeometry
						args={[
							COURT_DIMENSIONS.FREE_THROW_CIRCLE_RADIUS - 0.025,
							COURT_DIMENSIONS.FREE_THROW_CIRCLE_RADIUS + 0.025,
							64, // thetaSegments
							1, // phiSegments
							0, // thetaStart
							Math.PI * 2, // thetaLength
						]}
					/>
					{lineMaterial}
				</mesh>
			</group>
		);
	};

	// 3D Basketball Hoop
	const BasketballHoop = () => {
		const basketPos = courtToWorld(
			COURT_DIMENSIONS.WIDTH / 2,
			COURT_DIMENSIONS.BASKET_POSITION_FROM_BASELINE,
		);

		return (
			<group
				position={[basketPos.x, 0, basketPos.z]}
				rotation={[0, Math.PI, 0]}
			>
				{/* Rim */}
				<mesh
					position={[0, COURT_DIMENSIONS.BASKET_HEIGHT, 0]}
					rotation={[-Math.PI / 2, 0, 0]}
					castShadow
				>
					<torusGeometry args={[COURT_DIMENSIONS.BASKET_RADIUS, 0.02, 8, 32]} />
					<meshStandardMaterial
						color="#ff4500"
						metalness={0.8}
						roughness={0.2}
						polygonOffset
						polygonOffsetFactor={-2}
						polygonOffsetUnits={-2}
					/>
				</mesh>

				{/* Backboard */}
				<mesh
					position={[0, COURT_DIMENSIONS.BASKET_HEIGHT + 0.45, 0.375]}
					castShadow
					receiveShadow
				>
					<boxGeometry args={[1.8, 1.05, 0.05]} />
					<meshStandardMaterial color="#ffffff" transparent opacity={0.9} />
				</mesh>

				{/* Backboard rim box (red outline - not filled) */}
				<group position={[0, COURT_DIMENSIONS.BASKET_HEIGHT + 0.2, 0.35]}>
					{/* Top border */}
					<mesh position={[0, 0.225, 0]}>
						<boxGeometry args={[0.6, 0.034, 0.001]} />
						<meshStandardMaterial
							color="#C2452D"
							polygonOffset
							polygonOffsetFactor={-1}
							polygonOffsetUnits={-1}
						/>
					</mesh>
					{/* Bottom border */}
					<mesh position={[0, -0.225, 0]}>
						<boxGeometry args={[0.6, 0.034, 0.001]} />
						<meshStandardMaterial
							color="#C2452D"
							polygonOffset
							polygonOffsetFactor={-1}
							polygonOffsetUnits={-1}
						/>
					</mesh>
					{/* Left border */}
					<mesh position={[-0.3, 0, 0]}>
						<boxGeometry args={[0.034, 0.45, 0.001]} />
						<meshStandardMaterial
							color="#C2452D"
							polygonOffset
							polygonOffsetFactor={-1}
							polygonOffsetUnits={-1}
						/>
					</mesh>
					{/* Right border */}
					<mesh position={[0.3, 0, 0]}>
						<boxGeometry args={[0.034, 0.45, 0.001]} />
						<meshStandardMaterial
							color="#C2452D"
							polygonOffset
							polygonOffsetFactor={-1}
							polygonOffsetUnits={-1}
						/>
					</mesh>
				</group>

				{/* Support structure */}
				<mesh
					position={[0, COURT_DIMENSIONS.BASKET_HEIGHT, 0.75]}
					rotation={[Math.PI / 2, 0, 0]}
					castShadow
				>
					<cylinderGeometry args={[0.04, 0.04, 1.5]} />
					<meshStandardMaterial color="#333333" />
				</mesh>

				{/* Vertical pole */}
				<mesh
					position={[0, COURT_DIMENSIONS.BASKET_HEIGHT / 2, 1.5]}
					castShadow
				>
					<cylinderGeometry
						args={[0.05, 0.05, COURT_DIMENSIONS.BASKET_HEIGHT]}
					/>
					<meshStandardMaterial color="#333333" />
				</mesh>

				{/* Net (simplified) */}
				<group position={[0, COURT_DIMENSIONS.BASKET_HEIGHT - 0.3, 0]}>
					{Array.from({ length: 12 }).map((_, i) => {
						const angle = (i / 12) * Math.PI * 2;
						return (
							<mesh
								key={i}
								position={[
									Math.cos(angle) * COURT_DIMENSIONS.BASKET_RADIUS * 0.8,
									0,
									Math.sin(angle) * COURT_DIMENSIONS.BASKET_RADIUS * 0.8,
								]}
								rotation={[0, angle, 0]}
							>
								<cylinderGeometry args={[0.002, 0.002, 0.4]} />
								<meshStandardMaterial color="#ffffff" />
							</mesh>
						);
					})}
				</group>
			</group>
		);
	};

	// Three-point line (simplified arc)
	const ThreePointLine = () => {
		const lineMaterial = (
			<meshStandardMaterial
				color="#ffffff"
				polygonOffset
				polygonOffsetFactor={-1}
				polygonOffsetUnits={-1}
			/>
		);

		const basketPosWorldZ =
			-(COURT_DIMENSIONS.HEIGHT / 2) +
			COURT_DIMENSIONS.BASKET_POSITION_FROM_BASELINE;

		const radius = COURT_DIMENSIONS.THREE_POINT_RADIUS;
		const lineSideWorldX =
			COURT_DIMENSIONS.WIDTH / 2 - COURT_DIMENSIONS.THREE_POINT_CORNER_DISTANCE;

		// Z-coordinate in world space where arc meets the straight line
		const zIntersectWorld =
			basketPosWorldZ +
			Math.sqrt(radius * radius - lineSideWorldX * lineSideWorldX);

		// Straight lines
		const baselineWorldZ = -COURT_DIMENSIONS.HEIGHT / 2;
		const lineLength = zIntersectWorld - baselineWorldZ;
		const lineCenterWorldZ = (baselineWorldZ + zIntersectWorld) / 2;

		// Arc angles for ringGeometry
		const yForAtan = zIntersectWorld - basketPosWorldZ; // should be positive
		const startAngle = Math.atan2(yForAtan, lineSideWorldX);
		const endAngle = Math.atan2(yForAtan, -lineSideWorldX);
		const arcLength = endAngle - startAngle;

		return (
			<group>
				{/* Arc */}
				<mesh
					position={[0, 0.01, basketPosWorldZ]}
					rotation={[-Math.PI / 2, 0, -Math.PI]}
				>
					<ringGeometry
						args={[
							radius - COURT_DIMENSIONS.LINE_WIDTH / 2,
							radius + COURT_DIMENSIONS.LINE_WIDTH / 2,
							64, // segments
							1,
							startAngle,
							arcLength,
						]}
					/>
					{lineMaterial}
				</mesh>

				{/* Straight line - Right */}
				<mesh position={[lineSideWorldX, 0.01, lineCenterWorldZ]}>
					<boxGeometry args={[COURT_DIMENSIONS.LINE_WIDTH, 0.01, lineLength]} />
					{lineMaterial}
				</mesh>

				{/* Straight line - Left */}
				<mesh position={[-lineSideWorldX, 0.01, lineCenterWorldZ]}>
					<boxGeometry args={[COURT_DIMENSIONS.LINE_WIDTH, 0.01, lineLength]} />
					{lineMaterial}
				</mesh>
			</group>
		);
	};

	return (
		<group ref={courtGroup}>
			<CourtSurface />
			<CourtLines />
			<BasketballHoop />
			<ThreePointLine />
		</group>
	);
};
